/**
 * useViewerPasteBridge — the embedding page's half of the live view's paste
 * (2026-09-23, the D19a amendment; one copy since PR-5a-3, 2026-09-25, shared
 * by the sign-in window and the run's live view in the chat).
 *
 * The person's own paste gesture inside the live view (⌘V, Ctrl+V, right-click
 * → Paste) lands in viewer-keys.ts, which hands the text here, because only
 * this page holds the workstation credential. This posts it to the D19a route
 * with press:false: the route writes the remote browser's OWN clipboard and the
 * viewer, once answered, presses Ctrl+V where the person is typing. Why not
 * noVNC's own clipboard channel: x11vnc 0.9.16 answers the remote browser's
 * TARGETS request with the wrong type, so a paste through it landed 6–12 s late
 * (measured 2026-09-23).
 *
 * The viewer carries pastes only after this page's hello
 * (VIEWER_EMBEDDER_HELLO), answered to every report it makes. Only this
 * session's own frame, from the workstation's own origin, is heard
 * (readViewerMessage + the frame check); the text is never logged, stored or
 * shown, and no model sees it.
 */

import { useEffect, useRef, type RefObject } from 'react';
import { pushTextToDesktop } from '../desktopSignIn';
import {
  readViewerMessage,
  VIEWER_CLEAR_TEXT,
  VIEWER_EMBEDDER_HELLO,
  VIEWER_MSG_PASTE_DONE,
  type ViewerState,
} from '../viewerMessages';

export type ViewerPasteOutcome = Awaited<ReturnType<typeof pushTextToDesktop>>;

export interface ViewerPasteBridgeOptions {
  /** The desktop the frame shows; null while there is none. */
  session: { sessionId: string; base: string } | null;
  /** The live view's own frame: only its window is heard. */
  frameRef: RefObject<HTMLIFrameElement | null>;
  /** Every report the viewer makes (focus, Caps Lock, a paste too large). */
  onState?: (state: ViewerState) => void;
  /** After each paste the viewer handed over, once the viewer has been answered. */
  onPasted?: (outcome: ViewerPasteOutcome) => void;
}

export function useViewerPasteBridge(opts: ViewerPasteBridgeOptions): void {
  // The callbacks are read through refs, so a new function on every render
  // never re-subscribes the listener (and never drops a message in between).
  const onStateRef = useRef(opts.onState);
  onStateRef.current = opts.onState;
  const onPastedRef = useRef(opts.onPasted);
  onPastedRef.current = opts.onPasted;
  const { frameRef } = opts;
  const sessionId = opts.session?.sessionId ?? '';
  const base = opts.session?.base ?? '';

  useEffect(() => {
    if (!sessionId || !base) return undefined;
    let viewerOrigin = '';
    try {
      viewerOrigin = new URL(base).origin;
    } catch {
      return undefined;
    }
    const onMessage = (e: MessageEvent) => {
      const frameWindow = frameRef.current?.contentWindow;
      if (!frameWindow || e.source !== frameWindow) return;
      const msg = readViewerMessage(e.data, e.origin, viewerOrigin);
      if (!msg) return;
      const replyTo = e.source as Window;
      const replyOrigin = e.origin;
      if (msg.kind === 'state') {
        onStateRef.current?.(msg.state);
        // Every report is answered with the hello, so a viewer that reloaded
        // (a reconnect) learns again that this page carries pastes.
        try {
          replyTo.postMessage(VIEWER_EMBEDDER_HELLO, replyOrigin);
        } catch {
          /* the window closed meanwhile */
        }
        return;
      }
      if (msg.kind === 'clear') {
        void pushTextToDesktop(sessionId, base, VIEWER_CLEAR_TEXT, false);
        return;
      }
      void pushTextToDesktop(sessionId, base, msg.text, false).then((outcome) => {
        const ok = outcome === 'ok' || outcome === 'ok-pressed';
        try {
          replyTo.postMessage(
            { type: VIEWER_MSG_PASTE_DONE, v: 1, id: msg.id, ok, pressed: outcome === 'ok-pressed' },
            replyOrigin,
          );
        } catch {
          /* the window closed meanwhile */
        }
        onPastedRef.current?.(outcome);
      });
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [sessionId, base, frameRef]);
}
